import { useState } from 'react';
import {
    NSModal,
    NSButton,
    NSMultiSelect,
} from 'bricks';
import { useOrganizationSubprojects } from 'views/ProFormaTable/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { OptionType } from 'bricks/NSSelectWithCheckboxes/NSSelectWithCheckboxes';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { Link } from 'react-router-dom';
import { URLS } from 'services/urls';
import useToast from 'hooks/useToast';
import { IProjectCRMAssociation } from '../types';
import useUpsertProjectAssociations from './hooks/useUpsertProjectAssociations';
import getCRMDealProjectsQueryOptions from './hooks/useGetCRMDealProjects';
import { CRM_ENTITY_TYPE } from '../constants';

interface IUpsertProjectsModalProps {
    isOpen: boolean;
    toggle: () => void;
    organizationId: string;
    dealId: number;
    existingAssociations: IProjectCRMAssociation[];
    isLoadingExistingAssociations?: boolean;
}

const UpsertProjectsModal = ({
    isOpen, toggle, organizationId, dealId, existingAssociations, isLoadingExistingAssociations,
}: IUpsertProjectsModalProps) => {
    const queryClient = useQueryClient();
    const { showSuccess, showError } = useToast();

    const {
        data: orgProjects,
        isLoading: isLoadingOrgProjects,
    } = useOrganizationSubprojects({ orgId: organizationId, ownedByOrg: true });

    const [selectedProjects, setSelectedProjects] = useState<OptionType[]>(existingAssociations?.map(project => ({
        label: `${project.developmentProjectName} - ${project.subprojectName}`,
        value: project.subprojectCorrelationId,
    })) || [] as OptionType[]);

    const {
        mutate: upsertProjectAssociations,
        isPending: isSaving,
    } = useUpsertProjectAssociations({
        onSuccess: () => {
            showSuccess('Projects associated successfully');
            queryClient.invalidateQueries(getCRMDealProjectsQueryOptions(organizationId, dealId));
            toggle();
        },
        onError: error => {
            showError(`There was an error associating projects. Please try again. ${error?.message}`);
        },
    });

    const handleCreate = () => upsertProjectAssociations({
        organizationId,
        associationType: CRM_ENTITY_TYPE.DEAL,
        associationId: dealId,
        projectCorrelationIds: selectedProjects.map(project => project.value),
    });

    const options = orgProjects?.map(op => ({
        label: `${op.subproject.developmentProject.projectName} - ${op.subproject.subprojectName}`,
        value: op.subproject.correlationId,
    })) || [];

    return (
        <NSModal
            isOpen={isOpen}
            toggle={toggle}
            modalHeaderText="Associate projects"
            modalHeaderClassName="bg-primary text-white"
            customFooter={(
                <div>
                    <NSButton
                        text="Cancel"
                        className="mr-2"
                        outline={false}
                        callback={toggle}
                    />
                    <Link
                        to={URLS.PROJECTS_APP.PROJECT_SETUP}
                        target="_blank"
                        rel="noreferrer"
                        className="cursor--pointer"
                    >
                        <NSButton
                            text="Create new project"
                            color="secondary"
                            outline={false}
                            icon={faArrowUpRightFromSquare}
                            className="heap-deal-activity-create-new-project"
                        />
                    </Link>
                    <NSButton
                        className="ml-2 heap-deal-activity-add-project"
                        text="Save"
                        color="primary"
                        outline={false}
                        callback={handleCreate}
                        isLoading={isSaving}
                    />
                </div>
            )}
        >
            <NSMultiSelect
                options={options}
                selectName="add-projects"
                label="Projects"
                value={selectedProjects}
                onChange={setSelectedProjects}
                isClearable={false}
                componentClassName="mt-1"
                isLoading={isLoadingOrgProjects || isLoadingExistingAssociations}
            />

        </NSModal>
    );
};

export default UpsertProjectsModal;
